import { smsLength, type TemplateValues } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { ComposeShell } from '../compose/ComposeShell.js'
import { MessageBody, type TextRange } from '../compose/MessageBody.js'
import { DraftedBanner, MissingFields } from '../compose/Notices.js'
import { TemplatesMenu } from '../compose/TemplatesMenu.js'
import { initials, withTemplate } from '../compose/text.js'
import { useComposeAssistant, type MessageValue } from '../compose/useComposeAssistant.js'
import { useTextHistory } from '../compose/useTextHistory.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { dialable } from '../../lib/phone.js'

/**
 * A text is written here and then sent. With Telnyx set up (`sms.status`),
 * Send goes from the server and the window waits: it closes only once Telnyx
 * has taken the text, and otherwise stays open with the reason, as an email
 * does (decision 109). Without it, the text is handed to the phone with the
 * number and the words filled in, as before (decisions 86 and 95). Until
 * `sms.status` answers, or if it cannot, Send is the phone: that is the
 * floor. The Assistant writes to a text's length and never sends. There is
 * no Send later.
 */
export function SmsCompose(props: SmsComposeProps) {
  if (!props.open) return null
  return <SmsWindow {...props} />
}

export interface SmsComposeProps {
  open: boolean
  onClose: () => void
  contactName: string
  phone: string | null
  initialBody?: string
  /** The job it is about, so the Assistant knows what to suggest. */
  jobId?: string | null
  /** The text being answered. */
  replyToMessageId?: string | null
  /**
   * Called once the text has gone: when Telnyx took it (`server`), or as
   * Send hands it to the phone (`phone`), which is all the app can know then.
   * A booking link uses it to record that it went (decision 104).
   */
  onSend?: (via: 'server' | 'phone') => void
}

function SmsWindow({ onClose, contactName, phone, initialBody, jobId, replyToMessageId, onSend }: SmsComposeProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  // Whether this system sends texts itself. It changes only with the server's
  // setup, so it is not asked again on every open.
  const status = useQuery({ ...trpc.sms.status.queryOptions(), staleTime: 5 * 60_000, retry: false })
  const serverSend = status.data?.serverSend === true
  const send = useMutation(trpc.sms.send.mutationOptions())
  const [failure, setFailure] = useState<string | null>(null)
  const sending = send.isPending
  const history = useTextHistory<MessageValue>({ subject: '', body: initialBody ?? '' })
  const [range, setRange] = useState<TextRange | null>(null)
  const [missing, setMissing] = useState<string[]>([])
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const firstName = contactName.split(/\s+/)[0] || null

  const noFiles = useCallback(() => undefined, [])
  const assistant = useComposeAssistant({
    target: { channel: 'SMS', jobId, replyToMessageId },
    history,
    recipientFirstName: () => firstName,
    onAttach: noFiles,
    onRange: setRange,
  })
  const data = assistant.data
  const panel = assistant.context.isError || (data && !data.available) ? null : assistant

  const number = dialable(phone)
  const body = history.value.body.trim()
  const href = number && body ? `sms:${number}?body=${encodeURIComponent(body)}` : null
  const length = smsLength(history.value.body)
  const limit = length.perPart * Math.max(1, length.parts)
  const writing = assistant.drafting

  const templateValues = (): TemplateValues => ({
    ...(data?.values ?? {}),
    'contact.name': contactName || null,
    'contact.firstName': firstName,
  })

  const sendFromServer = async () => {
    if (!number || !body || writing || sending) return
    setFailure(null)
    try {
      await send.mutateAsync({ to: number, body, jobId: jobId ?? null, replyToMessageId: replyToMessageId ?? null })
      void queryClient.invalidateQueries(trpc.inbox.pathFilter())
      void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      void queryClient.invalidateQueries(trpc.compose.pathFilter())
      onSend?.('server')
      onClose()
    } catch (error) {
      setFailure(errorMessage(error))
    }
  }

  return (
    <ComposeShell
      channel="SMS"
      title="Text message"
      heading={data?.heading ?? null}
      assistant={panel}
      drop={null}
      onClose={onClose}
      footer={
        <>
          <TemplatesMenu
            channel="SMS"
            hasText={body !== ''}
            onPick={(template, mode) => {
              const result = withTemplate(history.value, range, template, mode, templateValues())
              history.commit({ subject: '', body: result.value.body })
              setMissing(result.missing)
              setRange({ start: result.caret, end: result.caret })
              requestAnimationFrame(() => bodyRef.current?.focus())
            }}
          />
          <div className="ml-auto flex items-center gap-2">
            {serverSend ? (
              <button
                type="button"
                disabled={!href || writing || sending}
                className="flex h-9 items-center gap-2 rounded-md bg-brand-500 px-4 text-[14px] font-semibold text-ink-900 hover:bg-brand-550 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => void sendFromServer()}
              >
                {sending ? <Spinner className="size-4" /> : <Icon name="send" className="size-4" />}
                Send
              </button>
            ) : href && !writing ? (
              <a
                href={href}
                className="flex h-9 items-center gap-2 rounded-md bg-brand-500 px-4 text-[14px] font-semibold text-ink-900 hover:bg-brand-550"
                onClick={() => {
                  onSend?.('phone')
                  onClose()
                }}
              >
                <Icon name="send" className="size-4" />
                Send
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="flex h-9 items-center gap-2 rounded-md bg-brand-500 px-4 text-[14px] font-semibold text-ink-900 opacity-50"
              >
                <Icon name="send" className="size-4" />
                Send
              </button>
            )}
          </div>
        </>
      }
    >
      <div className="flex min-h-11 items-center gap-3 border-b border-ink-100 py-1.5">
        <span className="w-14 shrink-0 text-[13px] text-ink-500">To</span>
        {number ? (
          <span className="flex min-w-0 items-center gap-1.5 rounded-md bg-ink-50 py-0.5 pr-3 pl-0.5 text-[13px] ring-1 ring-ink-200 ring-inset">
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center rounded-sm bg-ink-200 text-[10px] font-semibold text-ink-700"
            >
              {initials(contactName || phone || '?')}
            </span>
            <span className="truncate font-semibold text-ink-900">{contactName}</span>
            <span className="truncate text-ink-500">{phone}</span>
          </span>
        ) : (
          <span className="text-[13px] text-ink-500">No number on file.</span>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <DraftedBanner writing={writing} drafted={assistant.drafted} onUndo={assistant.undoDraft} onRedo={assistant.redoDraft} />
        <MessageBody
          id="sms-compose-body"
          label="SMS draft"
          value={history.value.body}
          textareaRef={bodyRef}
          placeholder={number ? `Text ${contactName}.` : 'Add a number before sending a text.'}
          range={range}
          busy={assistant.busy}
          readOnly={!number || writing || sending || assistant.busy !== null}
          canRewrite={Boolean(data?.available)}
          onType={(next) => history.type({ subject: '', body: next })}
          onRange={setRange}
          onRewrite={(action, target) => void assistant.rewrite(action, target)}
          onUndo={history.undo}
          onRedo={history.redo}
        />
        <p
          aria-live="polite"
          className={cn('self-end text-[12px] tabular-nums', length.parts > 1 ? 'font-medium text-ink-900' : 'text-ink-400')}
        >
          {length.characters} / {limit} · {length.parts || 1} SMS{length.unicode ? ' · emoji or accents use more room' : ''}
        </p>
        <MissingFields missing={missing} onDismiss={() => setMissing([])} />
        {failure ? (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {failure}
          </p>
        ) : null}
      </div>
    </ComposeShell>
  )
}
